import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple, ArrowRight, Check, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { formatAuthError } from "../lib/AuthContext";
import { AuthShell, Field } from "./LoginPage";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/request-password-reset", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Check your inbox" subtitle="If an account exists for that email, a reset link is on its way.">
        <div data-testid="forgot-pw-sent" className="flex items-start gap-2.5 p-3 border border-tac-primary/40 bg-tac-primary/8 rounded-sm">
          <Check size={16} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
          <div className="text-sm text-tac-text leading-relaxed">
            Reset links expire in 1 hour. Didn't get one? Double-check the address you typed,
            or wait a minute for it to arrive.
          </div>
        </div>
        <div className="text-[15px] text-tac-dim text-center mt-5">
          <Link to="/login" className="text-tac-primary hover:underline font-bold">
            ← Back to log in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll send you a reset link.">
      <form onSubmit={onSubmit} data-testid="forgot-pw-form" className="space-y-4">
        <Field
          icon={<EnvelopeSimple size={14} weight="bold" />}
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          testId="forgot-pw-email"
          required
        />
        {error && (
          <div data-testid="forgot-pw-error" className="flex items-start gap-2 text-xs text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2">
            <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          data-testid="forgot-pw-submit"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors"
        >
          {submitting ? "Sending…" : "Send reset link"}
          {!submitting && <ArrowRight size={14} weight="bold" />}
        </button>
      </form>
      <div className="text-[15px] text-tac-dim text-center mt-6">
        <Link to="/login" className="text-tac-primary hover:underline font-bold">← Back to log in</Link>
      </div>
    </AuthShell>
  );
}
